/**
 * @file PageBackgroundCard.tsx
 * @description «Фон страницы» и «Дизайн страницы» в настройках страницы
 * (phase19.16 Р2; uiux-design-r2 §3; db-design-r2 §2 — страница
 * переопределяет фон сайта частично; SB95б): [Как у сайта][Свой цвет][Фото]
 * + до 3 своих рисунков, дизайн страницы из сохранённых. Записывается
 * кнопкой в черновик страницы (`PATCH /pages/:id { background, designId }`; Р3а — на сайт после «Опубликовать»).
 *
 * SB122: карточка — группа `background` панели «Настройки страницы» со
 * сводкой «как у сайта / свой цвет / фото · рисунки · дизайн».
 */

import { DEFAULT_SITE_THEME, SECTION_BG_ROLES, sitePageBackgroundSchema, SITE_LIMITS, type SectionBgRole, type SitePageBackground } from "@chaiberi/shared";
import { Plus } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { Button } from "../../ui/button";
import { Label } from "../../ui/label";
import { SettingsGroup } from "../../ui/settings-groups";
import { SelectField } from "../../ui/SelectField";
import { useNotify } from "../../ui/NotifyProvider";
import { BackdropImageRow } from "../../site-view/appearance/BackdropImageRow";
import { PageBgImageSection } from "../../site-view/appearance/PageBgImageSection";
import { CheckRow } from "../../site-view/CheckRow";
import { MediaPickerDialog } from "../../site-view/media/MediaPickerDialog";
import { Segmented } from "../../site-view/Segmented";
import { useSiteView } from "../../site-view/site-view-context";
import { ColorField } from "../../site-fields/ColorField";
import { makeSectionFormValue, SectionFormContext } from "../section/section-form-context";
import { useBuilder } from "../builder-context";
import type { SitePageDetail } from "../../../lib/api-site-pages";
import { blockErrorText } from "../../../lib/site-builder/block-errors";
import { canonicalJson } from "../../../lib/site-builder/section-edit";
import { BG_ROLE_LABELS } from "../../../lib/site-builder/section-meta";
import { newBackdropImage } from "../../../lib/site-view/backdrop-images";

type Mode = "site" | "color" | "photo";

const MODES = [
  { value: "site" as const, label: "Как у сайта" },
  { value: "color" as const, label: "Свой цвет" },
  { value: "photo" as const, label: "Фото" },
];

/** Короткие слова режимов — для сводки свёрнутой группы. */
const MODE_SUMMARY: Readonly<Record<Mode, string>> = { site: "как у сайта", color: "свой цвет", photo: "фото" };

/** Цвет страницы, если это роль фона (образцы — роли фона секций). */
const bgRole = (bg: SitePageBackground | null): SectionBgRole | null =>
  bg?.color && (SECTION_BG_ROLES as readonly string[]).includes(bg.color) ? (bg.color as SectionBgRole) : null;

/** Роль фона страницы — заливкой для поля цвета; не роль — ничего не выбрано. */
function pageFill(bg: SitePageBackground | null): { kind: "role"; role: SectionBgRole } | null {
  const role = bgRole(bg);
  return role ? { kind: "role", role } : null;
}

/** Фото — своя картинка; «свой цвет» — цвет задан или картинка снята (`null`); иначе как у сайта. */
const modeOf = (bg: SitePageBackground | null): Mode => (bg?.image ? "photo" : bg?.color !== undefined || bg?.image === null ? "color" : "site");

export function PageBackgroundCard({ page }: { page: SitePageDetail }) {
  const b = useBuilder();
  const view = useSiteView();
  const notify = useNotify();
  const [bg, setBg] = useState<SitePageBackground | null>(page.background ?? null);
  const [designId, setDesignId] = useState<string | null>(page.designId ?? null);
  const [picking, setPicking] = useState<"photo" | "decor" | null>(null);
  const [saving, setSaving] = useState(false);
  // Сравнение без порядка ключей и undefined: после сохранения кнопка гаснет.
  const dirty = canonicalJson(bg) !== canonicalJson(page.background) || designId !== (page.designId ?? null);
  // Перечитанная страница (правка блока) не стирает несохранённый фон: форма
  // берёт значения сервера при смене страницы или когда своих правок нет.
  const shownPage = useRef(page.id);
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;
  useEffect(() => {
    if (shownPage.current === page.id && dirtyRef.current) return;
    shownPage.current = page.id;
    setBg(page.background ?? null);
    setDesignId(page.designId ?? null);
  }, [page.id, page.background, page.designId]);

  const theme = view.parts?.theme.theme ?? null;
  const decor = bg?.decor;
  const patch = (next: Partial<SitePageBackground>) => {
    const merged = { ...(bg ?? {}), ...next };
    setBg(Object.values(merged).every((v) => v === undefined) ? null : merged);
  };

  function onMode(mode: Mode) {
    if (mode === "site") patch({ color: undefined, image: undefined });
    // TD-332: свой цвет — картинки у страницы нет (`null`), а не «как у сайта».
    else if (mode === "color") patch({ color: bg?.color ?? "panelBg", image: null });
    else setPicking("photo");
  }

  async function save() {
    const parsed = bg === null ? null : sitePageBackgroundSchema.safeParse(bg);
    if (parsed && !parsed.success) {
      notify.error(parsed.error.issues[0]?.message ?? "Проверьте фон страницы.");
      return;
    }
    setSaving(true);
    try {
      await b.pages.update({ background: bg, designId });
      notify.success("Фон страницы сохранён в черновик — на сайте после «Опубликовать»");
    } catch (err) {
      notify.error(blockErrorText(err, "Не удалось сохранить фон страницы."));
    } finally {
      setSaving(false);
    }
  }

  const designs = b.designs.data?.items ?? [];
  const design = designId ? (designs.find((d) => d.id === designId)?.name ?? "свой") : null;
  // Несохранённый фон виден и у свёрнутой группы.
  const summary = [
    dirty ? "не сохранено" : null,
    MODE_SUMMARY[modeOf(bg)],
    decor === undefined || decor === null ? null : decor.length > 0 ? `рисунков ${decor.length}` : "без рисунков",
    design ? `дизайн «${design}»` : null,
  ]
    .filter(Boolean)
    .join(" · ");
  return (
    <SettingsGroup id="background" title="Фон страницы" tocLabel="Фон" summary={summary}>
      <SectionFormContext.Provider value={makeSectionFormValue([], [], () => undefined, theme)}>
        <Segmented aria-label="Фон страницы" options={MODES} value={modeOf(bg)} onChange={onMode} />
        {modeOf(bg) === "site" ? <p className="type-caption text-on-surface-variant">Фон из «Оформления» сайта. Прозрачные блоки показывают его.</p> : null}
        {modeOf(bg) === "color" ? (
          <ColorField
            mode="fill"
            label="Цвет фона страницы"
            hideLabel
            value={pageFill(bg)}
            roles={SECTION_BG_ROLES}
            labels={BG_ROLE_LABELS}
            colors={theme?.colors ?? DEFAULT_SITE_THEME.colors}
            // Схема фона страницы пока хранит только роль (`background.color`) — ячейки «Свой цвет» нет.
            custom={false}
            onChange={(fill) => (fill?.kind === "role" ? patch({ color: fill.role }) : undefined)}
          />
        ) : null}
        {bg?.image ? <PageBgImageSection image={bg.image} onBackdrop={(p) => p.image !== undefined && patch(p.image ? { image: p.image } : { image: null, color: bg.color ?? "pageBg" })} /> : null}
        <CheckRow checked={decor !== undefined} onChange={(on) => patch({ decor: on ? [] : undefined })}>
          Свои рисунки на этой странице
        </CheckRow>
        {decor !== undefined && decor !== null ? (
          <div className="flex flex-col gap-2">
            {decor.map((img, i) => (
              <BackdropImageRow key={`${img.mediaId}-${i}`} image={img} index={i} onChange={(next) => patch({ decor: next === null ? decor.filter((_, j) => j !== i) : decor.map((x, j) => (j === i ? next : x)) })} />
            ))}
            {decor.length < SITE_LIMITS.backdropImages ? (
              <Button variant="outline" className="self-start" onClick={() => setPicking("decor")}>
                <Plus aria-hidden />
                Добавить рисунок
              </Button>
            ) : null}
            {decor.length === 0 ? <p className="type-caption text-on-surface-variant">Без рисунков — даже если они есть у сайта.</p> : null}
          </div>
        ) : null}
      </SectionFormContext.Provider>
      <div className="flex flex-col gap-1">
        <Label>Дизайн страницы</Label>
        <SelectField
          ariaLabel="Дизайн страницы"
          value={designId ?? "common"}
          options={[{ value: "common", label: "Общий — как у сайта" }, ...designs.map((d) => ({ value: d.id, label: d.name }))]}
          onChange={(v) => setDesignId(v === "common" ? null : v)}
        />
        <p className="type-caption text-on-surface-variant">Цвета и шрифты этой страницы — из выбранного дизайна.</p>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button disabled={!dirty} loading={saving} onClick={() => void save()}>
          Сохранить фон
        </Button>
        {dirty ? (
          <Button variant="ghost" onClick={() => (setBg(page.background ?? null), setDesignId(page.designId ?? null))}>
            Отменить
          </Button>
        ) : null}
      </div>
      <MediaPickerDialog
        open={picking !== null}
        title={picking === "decor" ? "Рисунок фона страницы" : "Фото фона страницы"}
        onClose={() => setPicking(null)}
        onSelect={(media) => {
          const target = picking;
          setPicking(null);
          if (target === "decor") {
            patch({ decor: [...(decor ?? []), newBackdropImage(media.id, "br")] });
          } else {
            patch({ image: { mediaId: media.id, mode: "cover", opacity: 1, dim: 0, hideOnMobile: false }, color: undefined });
          }
        }}
      />
    </SettingsGroup>
  );
}
